import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import type {
  AuditLogResourceEnum,
  AuditLogSearchOutput,
  AuditLog,
} from '@repo/api-contract';
import { client } from '../../util';
import { pushToast } from '../toastSlice';

const pageSize = 15;

export interface AuditHistoryFormData {
  resource: AuditLogResourceEnum;
  resourceId: string;
  total: number;
  size: number;
  page: number;
  results: AuditLog[];
}

export interface AuditHistoryModalState {
  backToLabel: string;
  stateIsChanged: boolean;
  isOpenAuditHistoryModal: boolean;
  loading: boolean;
  tableLoading?: boolean;
  updateCallback?: (user: undefined) => Promise<void>;
  formData: AuditHistoryFormData;
}

const initialFormData: AuditHistoryFormData = {
  resource: 'customer', // We can't use empty value so we just use what ever as default
  resourceId: '',
  total: 0,
  size: 0,
  page: 0,
  results: [],
};

// Initialize state
const initialState: AuditHistoryModalState = {
  backToLabel: '',
  stateIsChanged: false,
  isOpenAuditHistoryModal: false,
  loading: false,
  tableLoading: false,
  formData: initialFormData,
};

export const auditHistoryModalSlice = createSlice({
  name: 'auditHistoryModal',
  initialState,
  reducers: {
    openAuditHistoryModal: (
      state,
      action: PayloadAction<
        | {
            loading?: boolean;
            backToLabel: string;
          }
        | undefined
      >,
    ) => {
      return {
        ...initialState,
        backToLabel: action.payload?.backToLabel || '',
        isOpenAuditHistoryModal: true,
        loading: action.payload?.loading ?? false,
        formData: {
          ...initialFormData,
        }, // Reset form data back to initial
      };
    },
    setFormData: (state, action: PayloadAction<AuditHistoryFormData>) => {
      // Update form data in redux whenever form changes
      return {
        ...state,
        formData: action.payload,
        stateIsChanged: true,
      };
    },
    closeModal: (
      state,
      action: PayloadAction<{ resetState?: boolean } | undefined>,
    ) => {
      if (action.payload?.resetState) return { ...initialState };
      return {
        ...state,
        isOpenAuditHistoryModal: false,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setTableLoading: (state, action: PayloadAction<boolean>) => {
      state.tableLoading = action.payload;
    },
  },
});

export const searchAuditLogs = async (
  resource: AuditLogResourceEnum,
  resourceId: string,
  page: number,
): Promise<[auditLogs: AuditLogSearchOutput | undefined, err: any]> => {
  try {
    setLoading(true);
    const auditLogs = await client.searchAuditLogs.query({
      queryParams: [
        { value: resource, key: 'resource', operator: 'must' },
        { value: resourceId, key: 'resourceId', operator: 'must' },
      ],
      size: pageSize,
      page: page,
    });
    return [auditLogs, undefined];
  } catch (err) {
    return [undefined, err];
  } finally {
    setLoading(false);
  }
};

export const openAuditHistoryModalThunk =
  (payload?: {
    resource?: AuditLogResourceEnum;
    resourceId: string;
    backToLabel: string;
  }) =>
  async (dispatch: Dispatch) => {
    dispatch(
      openAuditHistoryModal({
        loading: !!payload?.resource,
        backToLabel: payload?.backToLabel || '',
      }),
    );
    // if no resource, nothing to load
    const resource = payload?.resource;
    const resourceId = payload?.resourceId;
    if (!resource || !resourceId) return;

    dispatch(setLoading(true));
    const [auditLogs, err] = await searchAuditLogs(resource, resourceId, 0);
    if (err) {
      pushToast({ message: 'Unable to get history', type: 'error' });
      dispatch(closeModal({ resetState: true }));
      return;
    }

    dispatch(
      setFormData({
        resource,
        resourceId,
        total: auditLogs?.total ?? 0,
        size: auditLogs?.size ?? 0,
        page: auditLogs?.page ?? 0,
        results: auditLogs?.results ?? [],
      }),
    );
    dispatch(setLoading(false));
  };

export const loadMoreAuditHistoryThunk =
  () => async (dispatch: Dispatch, getState: () => any) => {
    const state: AuditHistoryModalState = getState().auditHistoryModalSlice; // <-- adjust key if different

    if (!state.isOpenAuditHistoryModal) return;
    if (state.loading || state.tableLoading) return;

    const { total, size, page, results, resource, resourceId } = state.formData;

    // Stop if we already loaded everything
    if (total > 0 && results.length >= total) return;

    const nextPage = page + 1;

    dispatch(setTableLoading(true));
    try {
      const auditLogs = await client.searchAuditLogs.query({
        queryParams: [
          { value: resource, key: 'resource', operator: 'must' },
          { value: resourceId, key: 'resourceId', operator: 'must' },
        ],
        size: size || pageSize,
        page: nextPage,
      });

      const newResults = auditLogs?.results ?? [];

      dispatch(
        setFormData({
          ...state.formData,
          total: auditLogs?.total ?? total,
          size: auditLogs?.size ?? (size || pageSize),
          page: auditLogs?.page ?? nextPage,
          results: [...results, ...newResults],
        }),
      );
    } catch (err) {
      pushToast({ message: 'Unable to load more history', type: 'error' });
    } finally {
      dispatch(setTableLoading(false));
    }
  };

export const {
  openAuditHistoryModal,
  closeModal,
  setLoading,
  setTableLoading,
  setFormData,
} = auditHistoryModalSlice.actions;

export default auditHistoryModalSlice.reducer;
